import { useEffect } from 'react'

import { useAuth } from '@clerk/nextjs'
import { setUser as setSentryUser } from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { useQuery } from 'urql'

import useLogout from 'domains/user/use-logout'
import { setUserIdToLocalStorage } from 'domains/user/user.storage'
import { websitePaths } from 'domains/website/website.paths'
import { ViewerDocument } from 'graphql/schema/user'
import { contactEmail } from 'lib/constants/grafbase'
import { saveCookieChoice } from 'lib/utils/cookies'
import { openInNewTab } from 'lib/utils/links'
import { toasty } from 'store/toasts.state'

import { FCC } from 'types/react'

/**
 * Loads viewer (And identifies on different platform) at top website level and handles null/error to logout or retry
 */
const LoadUser: FCC = ({ children }) => {
  const { route } = useRouter()
  const { isSignedIn } = useAuth()
  const { logout } = useLogout()
  const pause = !isSignedIn || [websitePaths.authCallback].includes(route)
  const [{ fetching, error, data }] = useQuery({
    query: ViewerDocument,
    pause
  })

  useEffect(() => {
    if (!data?.viewer) {
      return
    }

    // User is logged in, we auto-accept cookies
    saveCookieChoice(true)

    const { id, name, email, avatarUrl } = data.viewer
    // Later usage without need to call the viewer query
    setUserIdToLocalStorage(id)

    const { personalAccount, createdAt } = data.viewer

    // Sentry
    setSentryUser({
      id,
      username: personalAccount?.slug,
      email
    })

    // RudderStack
    if (window.rudderanalytics?.getUserId?.() !== id) {
      window?.rudderanalytics?.identify(id, {
        name,
        email,
        createdAt,
        username: personalAccount?.slug,
        avatar: avatarUrl,
        theme: document.documentElement.className
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.viewer])

  // Errors
  useEffect(() => {
    if (pause || fetching) {
      return
    }

    if (error?.graphQLErrors) {
      if (isSignedIn) {
        logout().then(
          () =>
            toasty?.error({
              title: 'Unable to load session',
              text: 'Please, try to sign in again. If the problem persists contact support',
              button: {
                variant: 'secondary',
                children: 'Contact',
                onClick: () => openInNewTab(`mailto:${contactEmail}`)
              }
            })
        )
      }
    }
  }, [error, fetching, isSignedIn, pause, logout])

  return <>{children}</>
}

export default LoadUser
